import { SerializableObject } from '../../common-react/core';

import { CustomsInfo } from './customs-info.do';
import { ShipmentDocument } from './shipment-document.do';
import { ShipmentTrackingEvent } from './shipment-tracking-event.do';

export class ShipmentDetails extends SerializableObject {
	public trackingEvents: ShipmentTrackingEvent[];
	public documents: ShipmentDocument[];
	public customsInfo?: CustomsInfo;

	constructor(json?: unknown) {
		super();
		this.trackingEvents = [];
		this.documents = [];
		this.buildFromJSON(json);
	}

	public buildFromJSON(json: any): void {
		if (!json || typeof json !== 'object' || Array.isArray(json)) {
			return;
		}

		const normalizedJson = {
			trackingEvents: this.getCollection(json.trackingEvents || json.events),
			documents: this.getCollection(json.shipmentDocuments ?? json.documents),
			customsInfo: json.customsInfo || json.customsInformation,
		};

		super.buildFromJSON(normalizedJson);
	}

	protected coercePropertyType(propertyName: string, propertyValue: unknown): unknown {
		switch (propertyName) {
			case 'trackingEvents':
				return this.getCollection(propertyValue).map(value => this.mapTrackingEvent(value));

			case 'documents':
				return this.getCollection(propertyValue).map(value => new ShipmentDocument(value));

			case 'customsInfo':
				return propertyValue ? new CustomsInfo(propertyValue) : undefined;

			default:
				return super.coercePropertyType(propertyName, propertyValue);
		}
	}

	private getCollection(value: any): unknown[] {
		if (Array.isArray(value)) {
			return value;
		}
		if (!value || typeof value !== 'object') {
			return [];
		}
		if (Array.isArray(value.items)) {
			return value.items;
		}
		if (Array.isArray(value.results)) {
			return value.results;
		}
		return [];
	}

	private mapTrackingEvent(value: unknown): ShipmentTrackingEvent {
		if (!value || typeof value !== 'object') {
			return new ShipmentTrackingEvent(value);
		}

		const event = { ...(value as Record<string, any>) };
		event.timestamp = event.timestamp || event.dateTime || event.timeStamp || this.getTimestamp(event.date, event.time);
		event.title = event.title || event.statusText || event.description;
		event.description = event.description || event.title;

		if (typeof event.location === 'string') {
			event.location = { name: event.location };
		} else if (!event.location && typeof event.locationName === 'string') {
			event.location = { name: event.locationName };
		}

		return new ShipmentTrackingEvent(event);
	}

	private getTimestamp(date?: string, time?: string): string {
		if (!date) {
			return undefined;
		}
		const dateMatch = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(date);
		if (dateMatch) {
			return `${dateMatch[3]}-${dateMatch[2]}-${dateMatch[1]}T${time || '00:00'}`;
		}
		return time ? `${date}T${time}` : date;
	}
}
